<template>
  <div class="page-max-width footer-max-width">
    <QHeader class="bg-primary text-white">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="goBack" />
        <QToolbarTitle>
          {{ $t("ETAB.PAGES.ACCOUNT.@TITLE") }}
        </QToolbarTitle>
      </QToolbar>
    </QHeader>
    <QPageContainer>
      <QPage padding>
        <div v-if="!loading" class="admin-folder-status-page row justify-center">
          <section class="q-gutter-y-md q-mt-lg full-width">
            <IconLabel heavy no-padding :label="$t('ETAB.PAGES.ADMIN_FOLDER.@ADMIN_FOLDER_STATUS_LABEL')" icon="fad fa-folder-bookmark" />
            <template v-if="hasPaymentMean">
              <h5 class="text-weight-bold">
                {{ $t("ETAB.PAGES.ADMIN_FOLDER.@CHOSEN_PAYMENT_MEAN_LABEL") }}
              </h5>
              <QBanner rounded class="bg-primary-lighter">
                {{ $t("ETAB.PAGES.ADMIN_FOLDER.@CHOSEN_PAYMENT_MEAN_TEXT", { label: paymentMeanLabel }) }}
              </QBanner>
            </template>
            <QList bordered padding class="rounded-borders admin-folder-status-page-list">
              <!-- Administrative informations -->
              <QItemLabel header>
                {{ $t("ETAB.PAGES.ADMIN_FOLDER.MENU.@ADMINISTRATIVE_INFOS") }}
              </QItemLabel>

              <QItem v-ripple clickable :to="etabAdminFolderRoute" class="bg-primary-ultra-light">
                <QItemSection avatar>
                  <QIcon name="fad fa-pen-field" color="primary" size="md" />
                </QItemSection>

                <QItemSection>
                  <QItemLabel class="text-bold">
                    {{ adminFolderText }}
                  </QItemLabel>
                </QItemSection>

                <QItemSection side>
                  <QIcon v-if="isAdminFolderCompleted" name="fad fa-check" color="extra-green" />
                  <QIcon v-else name="fad fa-circle-exclamation" color="secondary" />
                </QItemSection>
              </QItem>
            </QList>

            <QList bordered padding class="rounded-borders">
              <!-- IBAN -->
              <QItemLabel header>
                {{ $t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_IBAN_LABEL") }}
              </QItemLabel>
              <QItem v-ripple :to="ibanItemTo" :href="ibanItemHref" :target="ibanItemTarget" class="bg-primary-ultra-light">
                <QItemSection avatar>
                  <QIcon name="fad fa-building-columns" color="primary" size="md" />
                </QItemSection>

                <QItemSection>
                  <QItemLabel class="text-bold">
                    {{ ibanStatusText }}
                  </QItemLabel>
                </QItemSection>

                <QItemSection side>
                  <QIcon v-if="registeredIban" name="fad fa-check" color="extra-green" />
                  <QIcon v-else name="fad fa-circle-exclamation" color="secondary" />
                </QItemSection>
              </QItem>

              <!-- cards -->
              <QSeparator spaced />
              <QItemLabel header>
                {{ $t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_CARD_LABEL") }}
              </QItemLabel>

              <QItem v-ripple :to="etabCardsRoute" class="bg-primary-ultra-light">
                <QItemSection avatar>
                  <QIcon name="fad fa-credit-card" color="primary" size="md" />
                </QItemSection>

                <QItemSection>
                  <QItemLabel class="text-bold">
                    {{ $t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_SET_CREDIT_CARD") }}
                  </QItemLabel>
                </QItemSection>

                <QItemSection side>
                  {{ $tc("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_CARDS_COUNT", cardsCount) }}
                </QItemSection>
              </QItem>
            </QList>
            <ExtraExpansionTip
              v-if="registeredIban"
              :title="$t('ETAB.PAGES.PAYMENT.ETAB_IBAN.@UPDATE_IBAN_TIP_TITLE')"
              content-class="text-caption"
              class="rounded-borders"
            >
              <span>{{ $t("ETAB.PAGES.PAYMENT.ETAB_IBAN.@UPDATE_IBAN_TIP") }}</span>
            </ExtraExpansionTip>
          </section>
        </div>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script>
  import { isMobile, StatusBar } from "@core/plugins/mobile";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { mapState } from "pinia";
  import { useProfileStore } from "@etab/stores/profile";
  import { ETAB_ROUTES } from "@etab/config";
  import { ETAB_PAYMENT_MEANS_TYPES, GO_CARDLESS } from "@core/constants";
  import { ACTION_ADMIN_FOLDER_INIT, GETTER_ADMIN_FOLDER_GET, GETTER_PROFILE_GET } from "@etab/stores/types";

  export default {
    data() {
      return {
        loading: false,
      };
    },
    computed: {
      ...mapState(useProfileStore, {
        profile: GETTER_PROFILE_GET,
        etabAdminFolder: GETTER_ADMIN_FOLDER_GET,
      }),
      ibanItemTo() {
        return this.hasEpctFeatureFlag ? this.etabIbanFormRoute : undefined;
      },
      ibanItemHref() {
        return !this.hasEpctFeatureFlag && !this.registeredIban ? GO_CARDLESS : undefined;
      },
      ibanItemTarget() {
        return !this.hasEpctFeatureFlag && !this.registeredIban && !isMobile ? "_blank" : undefined;
      },
      hasEpctFeatureFlag() {
        return this.profile.hasEpctFeatureFlag();
      },
      isAdminFolderCompleted() {
        return this.etabAdminFolder.getHasAdminInfo();
      },
      hasPaymentMean() {
        return this.paymentMean && this.paymentMean !== ETAB_PAYMENT_MEANS_TYPES.NONE.code;
      },
      paymentMeanLabel() {
        const paymentMeanObject = Object.values(ETAB_PAYMENT_MEANS_TYPES).find((value) => value.code === this.paymentMean);
        return paymentMeanObject ? this.$t(`${paymentMeanObject.label}`) : "";
      },
      hasCard() {
        return this.etabAdminFolder.getHasCard();
      },
      cardsCount() {
        return this.etabAdminFolder.getCardsCount();
      },
      registeredIban() {
        return this.etabAdminFolder.getRegisteredIban();
      },
      paymentMean() {
        return this.etabAdminFolder.getPaymentMean();
      },
      ibanStatusText() {
        return this.registeredIban
          ? this.$t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_REGISTERED_IBAN")
          : this.$t("ETAB.PAGES.ADMIN_FOLDER.@PAYMENT_MEAN_SET_IBAN");
      },
      adminFolderText() {
        return this.isAdminFolderCompleted
          ? this.$t("ETAB.PAGES.ADMIN_FOLDER.@ADMIN_FOLDER_FILLED_ADMIN_INFOS")
          : this.$t("ETAB.PAGES.ADMIN_FOLDER.@ADMIN_FOLDER_FILL_ADMIN_INFOS");
      },
    },
    created() {
      this.getEtabAdminFolder();
      this.initRoute();
      this.GO_CARDLESS = GO_CARDLESS;
    },
    methods: {
      initRoute() {
        this.etabAdminFolderRoute = { name: ETAB_ROUTES.account.adminFolder.name };
        this.etabIbanFormRoute = { name: ETAB_ROUTES.payment.iban.name, query: { url: this.$route.path } };
        this.etabCardsRoute = { name: ETAB_ROUTES.payment.cards.name };
      },
      async getEtabAdminFolder() {
        const profileStore = useProfileStore();
        try {
          this.$_startGlobalLoading();
          this.loading = true;
          await profileStore[ACTION_ADMIN_FOLDER_INIT]();
        } catch (error) {
          NotificationsManager.error();
          throw error;
        } finally {
          this.loading = false;
          this.$_stopGlobalLoading();
        }
      },
      goBack() {
        this.$router.push({ name: ETAB_ROUTES.userMenu.root.name }).catch((suppressedError) => {});
        StatusBar.setDefault();
      },
    },
  };
</script>

<style lang="scss">
  .admin-folder-status-page {
    width: 100%;
    max-width: $breakpoint-lg-max;
    .admin-folder-status-page {
      flex: 1;
    }
  }
</style>
